import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionItem,
  Button,
  Link,
  Loader,
  TextDirections,
  Title,
} from '@express-labs/raven-ui';
import {
  cn,
  getStateAbbreviation,
} from '@express-labs/raven-tools';
import s from './StoreDetails.module.scss';

function LoginContainer() {
  return (
    <>
      <Link
        as="a"
        theme="dark"
        size="body"
        href="/login"
      >
        Sign in
      </Link>
      &nbsp;or&nbsp;
      <Link
        as="a"
        theme="dark"
        size="body"
        href="/register"
      >
        Create an Account
      </Link>
      &nbsp;to make this My Store.
    </>
  );
}

function StoreDetails({
  customerStore,
  isLoggedIn,
  preferredStore,
  onSetPreferredStore,
  onSendTextMessage,
  error,
  setPreferredStoreStatus,
}) {

  const [loading, setLoading] = useState(false);

  const {
    storeNumber,
    name,
    addressLine1,
    city,
    distance,
    state,
    postalCode,
    country,
    phoneNumber,
    hoursOfOperation,
    weeklyHoursOfOperation,
  } = customerStore ?? {};

  const {
    monday, friday, saturday, sunday,
  } = weeklyHoursOfOperation ?? {};

  const onSetMyStore = () => {
    setLoading(true);
    onSetPreferredStore(customerStore);
  };

  const handleSendText = ({ storeNumber: textStore, phoneNumber: textPhone }) => {
    onSendTextMessage({ storeNumber: textStore, phoneNumber: textPhone });
  };

  const parseClosingTime = (hours) => {
    try {
      const openUntil = 'Open today until';
      if (hours && hours.toLowerCase() !== 'closed') {
        const rawClosingTime = hours.split('-')[1];
        const closingTime = rawClosingTime.slice(0, -2);
        const closingMeridiem = rawClosingTime.slice(-2);
        /* istanbul ignore next */
        if (closingTime.indexOf(':') === -1) {
          return `${openUntil} ${closingTime}:00 ${closingMeridiem}`;
        }
        return `${openUntil} ${closingTime} ${closingMeridiem}`;
      }
      return 'Closed';
    } catch (e) {
      /* istanbul ignore next */
      return '';
    }
  };

  useEffect(() => {
    if (setPreferredStoreStatus || error) {
      setLoading(false);
    }
  }, [setPreferredStoreStatus, error]);

  return (
    <div className={s.storeDetailsWrapper}>
      <Title tag="h5" className={s.storeNameBopis}>{name}</Title>
      <p className={cn(s.storeNameBopis, 'bodySecondary')} data-testid="storeInfo">
        {parseClosingTime(hoursOfOperation)}
      </p>
      <p className={cn(s.storeNameBopis, 'bodySecondary')}>
        {distance}
        {' '}
        miles away
      </p>
      <Accordion className={s.storeInfoAccordionWrapper}>
        <AccordionItem
          label="Store Details"
          dataAccordionId="1"
          className={s.storeInfoAccordion}
          labelClassName={s.storeInfoAccordionLabel}
          panelClassName={s.storeInfoAccordionPanel}
          
        >
          <div
            key={`store-details-view_${name}`}
            id={`store-details-view_${name}`}
            className={s.locationChangeForm}
            aria-labelledby={`view-store-details_${name}`}
          >
            <div className={s.storeDetailsDiv}>
              <div className={cn(s.storeDetailsContainer, s.bopisStoreDetails)}>
                <Title tag="h5">Store location</Title>
                <p className={cn(s.storeInfo, 'bodySecondary')}>{addressLine1}</p>
                <p className={cn(s.storeInfo, 'bodySecondary')}>{`${city},${state} ${postalCode}`}</p>
                <p className={cn(s.storeInfo, 'bodySecondary')}>{phoneNumber}</p>
                <p className={cn(s.storeInfo)}>
                  <Link
                    as="a"
                    theme="dark"
                    size="body"
                    href={`https://maps.google.com/maps?&daddr=${addressLine1},${city},${getStateAbbreviation(state?.trim())},${postalCode},${country}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(s.storeLocationLinks, 'bodySecondary')}
                    title={`Get direction to ${name} from Google maps`}
                  >
                    Get Directions
                  </Link>
                  <br />
                  <Link
                    as="a"
                    theme="dark"
                    size="body"
                    href={`https://stores.express.com/${storeNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.storeLocationLinks}
                    title={`Visit ${name} store page`}
                  >
                    Visit Store Page
                  </Link>
                </p>
                <Title tag="h5">Store Hours</Title>
                <ul className={s.storeHoursList}>
                  <li>
                    Mon-Thurs:
                    {' '}
                    {monday}
                  </li>
                  <li>
                    Friday:
                    {' '}
                    {friday}
                  </li>
                  <li>
                    Saturday:
                    {' '}
                    {saturday}
                  </li>
                  <li>
                    Saturday:
                    {' '}
                    {sunday}
                  </li>
                </ul>
                <p className='bodySecondary'>
                  {error
                    && (
                      <span className={s.error}>
                        An error occurred. Please try again.
                      </span>
                    )}
                  {!isLoggedIn && <LoginContainer />}
                  {isLoggedIn
                    && preferredStore === storeNumber
                    && (
                      <span>
                        My Store
                      </span>
                    )}
                  {isLoggedIn
                    && preferredStore !== storeNumber
                    && (
                      <span>
                        {loading ? <Loader data-loader="StoreDetails submitting" data-testid="loader" size="mini" chroma="dark" display="inline" />
                          : (
                            <Button
                              as="button"
                              theme="dark"
                              size="small"
                              onClick={onSetMyStore}
                            >
                              Set as My Store
                            </Button>
                          )}
                      </span>
                    )}
                </p>

                <Accordion>
                  <AccordionItem
                    label="Text Address"
                    dataAccordionId="2"
                    className={s.accordion}
                    labelClassName={s.accordionItem}
                  >
                    <TextDirections
                      onTextAddress={handleSendText}
                      storeNumber={storeNumber}
                    />
                    <p className='bodyTertiary'>
                      * By providing your mobile number you will receive a
                      mobile message with the requested Express location. #
                      of msgs depend on # of locations requested. Message and
                      Data Rates May Apply. Information collected here will
                      only be used to send a one-time message on your behalf.
                    </p>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

StoreDetails.propTypes = {
  customerStore: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    bopisAvailabilityMessage: PropTypes.string,
    bopisEligible: PropTypes.bool,
    bopisMessage: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    distance: PropTypes.number,
    hoursOfOperation: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    name: PropTypes.string,
    phoneNumber: PropTypes.string,
    postalCode: PropTypes.string,
    skuAvailability: PropTypes.shape({
      availabilityMessage: PropTypes.string,
      isAvailable: PropTypes.bool,
      isEligible: PropTypes.bool,
      quantity: PropTypes.number,
      sku: PropTypes.string,
    }),
    state: PropTypes.string,
    storeId: PropTypes.string,
    storeNumber: PropTypes.string,
    weeklyHoursOfOperation: PropTypes.shape({
      monday: PropTypes.string,
      tuesday: PropTypes.string,
      wednesday: PropTypes.string,
      thursday: PropTypes.string,
      friday: PropTypes.string,
      saturday: PropTypes.string,
      sunday: PropTypes.string,
    }),
  }),
  isLoggedIn: PropTypes.bool,
  error: PropTypes.string,
  onSendTextMessage: PropTypes.func.isRequired,
  onSetPreferredStore: PropTypes.func.isRequired,
  preferredStore: PropTypes.string,
  setPreferredStoreStatus: PropTypes.bool,
};

StoreDetails.defaultProps = {
  customerStore: {
    addressLine1: undefined,
    city: undefined,
    country: undefined,
    distance: undefined,
    hoursOfOperation: undefined,
    name: undefined,
    phoneNumber: undefined,
    postalCode: undefined,
    state: undefined,
    storeId: undefined,
    storeNumber: undefined,
    weeklyHoursOfOperation: {},
  },
  error: null,
  isLoggedIn: false,
  preferredStore: null,
  setPreferredStoreStatus: null,
};

export default StoreDetails;
